import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getQuestionsApi } from 'api/reviews';
import { sendRequest } from 'api/sendRequest';

import { endpoints } from 'constants/endpoints';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { getOrganizationTags } from 'services/admin';
import { getQuestionById } from 'services/reviews';

import { EngagementDriverType } from '../../types';
import { EngagementQuestionType } from '../slice/AddEngagementQuestion.slice';

const getDriversList = async () => {
  try {
    // @ts-expect-error argument mismatch
    const orgTags = await getOrganizationTags({ teamId: -1, tagType: 'ENGAGEMENT_DRIVER' });
    return orgTags.filter((tag) => tag.active);
  } catch (error) {
    console.error('Error in getDriversList', error);
    return [];
  }
};

export const useGetEngagementDriversList = () =>
  useQuery<Array<EngagementDriverType>>({
    queryKey: [REACT_QUERY_KEYS.engagement.engagementDriversList, 'ENGAGEMENT_DRIVER'],
    queryFn: getDriversList,
  });

const saveEngagementQuestionApi = async (question: EngagementQuestionType, isArchive: boolean | null = false) => {
  try {
    const url = endpoints.reviews.question.index;
    return sendRequest({
      url,
      method: 'PUT',
      credentials: 'include',
      body: question,
      queryParams: { isArchive },
    });
  } catch (error) {
    console.error('Error in saveEngagementQuestionApi', error);
    return null;
  }
};

export const useMutateAddEngagementQuestion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ question, isArchive }: { question: EngagementQuestionType; isArchive?: boolean }) =>
      saveEngagementQuestionApi(
        {
          ...question,
        },
        isArchive
      ),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onSuccess: (_, { question, isArchive }) => {
      queryClient.invalidateQueries([REACT_QUERY_KEYS.engagement.engagementQuestionsList]);
    },
  });
};

type AnswerOption = {
  id: number;
  description: string;
  score: number;
  displayOrder: number | null;
  questionId: number;
};

type Tag = {
  active: boolean;
  uuid: string;
  createdTS: number;
  updatedTS: number;
  organizationId: number;
  team: string | null;
  name: string;
  displayName: string;
  meshTags: string | null;
  tagType: string;
  imageUrl: string | null;
  description: string;
  color: string | null;
  parentTag: string | null;
  budgetPeriod: string | null;
  awardMoney: string | null;
  tagSubType: string;
};

export type ResponseEngagementQuestionType = {
  id: number;
  organizationId: number;
  description: string;
  answerType: string;
  questionType: string;
  isArchived: boolean;
  isViewOnlyModeEnabled: boolean;
  enableFeedbackText: boolean;
  answerOptions: AnswerOption[];
  selectedAnswerOptions: null;
  feedbackText: null;
  feedbackTextHtml: null;
  analyticsQuestionType: string;
  tag: Tag;
  isTextRequired: boolean;
  minOptions: number | null;
  maxOptions: number | null;
  isRequired: boolean;
  formType: string;
  isPredefined: boolean;
  hasCustomDescription: boolean;
  maxRating: number;
};

const getQuestionsList = async ({
  offset,
  limit,
  label,
  source,
}: {
  offset: number;
  limit: number;
  label: string;
  source: string;
}) => {
  try {
    const res = await getQuestionsApi({ offset, limit, label, source });
    return res?.entity as ResponseEngagementQuestionType[];
  } catch (error) {
    console.error('Error in engagement getQuestionsList', error);
    return [];
  }
};

export const useGetEngagementQuestionsList = ({
  offset,
  limit,
  label,
  source,
}: {
  offset: number;
  limit: number;
  label: string;
  source: string;
}) =>
  useQuery({
    queryKey: [REACT_QUERY_KEYS.engagement.engagementQuestionsList, offset, limit, label, source],
    queryFn: () =>
      getQuestionsList({
        offset,
        limit,
        label,
        source,
      }),
  });

export const useGetEngagementQuestionById = ({ questionId, enabled }: { questionId: number; enabled: boolean }) =>
  useQuery({
    queryKey: [REACT_QUERY_KEYS.engagement.engagementQuestion, questionId],
    queryFn: () => getQuestionById({ questionId: String(questionId) }),
    enabled,
  });
